import { getReceipt } from '@/api';
import ReceiptMailModal from '@/components/Airport/ReceiptMailModal';
import ReceiptForPrint from '@/components/Receipt/ReceiptForPrint';
import ReceiptLayout from '@/components/Receipt/ReceiptLayout';
import { routes } from '@/constants/routes.const';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import { formatPrintReceiptForm } from '@/utils/format.util';
import { IMG_RECEIPT_PREVIEW2 } from '@repo/assets';
import { ReceiptSummary } from '@repo/components';
import { isRefundableAirport } from '@repo/utils';
import { Button, Icon, Popover } from '@successmode-ktp/kui';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router';
import styled from 'styled-components';

const TEXT = {
  stepTitle: [
    'What should I do after printing the receipt?',
    '영수증을 출력한 뒤 어떻게 해야하나요?',
  ],
  step1: [
    'Bring the printed refund receipt and purchased items to Customs.',
    '출력한 환급 영수증과 구매물품을 가지고 세관에 방문해주세요.',
  ],
  step2: [
    'Show the printed refund receipt at the refund location and receive your refund.',
    '환급창구에 출력한 환급 영수증을 보여주고 환급금을 받으세요.',
  ],
  stepEtc: [
    'Submit your refund receipt to Customs.',
    '세관에 출력한 환급 영수증을 제출하세요',
  ],
  bodyText: [
    'You can email it to yourself\nand print it at the airport.',
    '본인 이메일로 전송하여\n공항에서 출력하세요.',
  ],
  bodyTextCruise: [
    'You can email it to yourself and print it',
    '본인 이메일로 전송하여 출력하세요',
  ],
  sendButton: ['Send receipts to my E-mail', '내 이메일로 보내기'],
  printButton: ['Printer Location', '프린터 위치'],
};

function AirportPrintReceipt() {
  const { passportNumber, nationality, refundAfter } = useAppSelector(
    (state) => state,
  ) as IGlobalState;
  const isCruise = passportNumber
    ? localStorage.getItem(passportNumber) === 'Cruise'
    : false;
  const [isOpenMailModal, setIsOpenMailModal] = useState(false);
  const [isOpenToggle, setIsOpenToggle] = useState(false);
  const [receiptInfo, setReceiptInfo] = useState<any | null>(null);
  const history = useHistory();
  const isMainAirport =
    passportNumber && isRefundableAirport(localStorage.getItem(passportNumber));

  const { data: receiptData } = useQuery(
    ['storeReceiptDetailList'],
    () =>
      getReceipt({
        passportNumber,
        latest: true,
        refundAfter,
      }),
    { enabled: !!passportNumber },
  );

  const nationIndex = nationality === 'KOR' ? 1 : 0;

  return (
    <>
      <ReceiptLayout>
        <Header>
          <Title>
            <button type='button' onClick={() => history.goBack()}>
              <Icon.ChevronLeft />
            </button>
            {nationality === 'KOR'
              ? '환급전표 출력'
              : 'Print My Refund Receipts'}
            <ToggleButton onClick={() => setIsOpenToggle((prev) => !prev)}>
              <Icon.QuestionFilled size={24} fillColor='#9FA1A7' />
            </ToggleButton>
          </Title>
          {isOpenToggle && (
            <StepList>
              <StepTitle>{TEXT.stepTitle[nationIndex]}</StepTitle>
              <StepItem>
                <div>
                  <Count>1</Count>
                </div>
                {TEXT.step1[nationIndex]}
              </StepItem>
              <StepItem>
                <div>
                  <Count>2</Count>
                </div>
                {isMainAirport
                  ? TEXT.step2[nationIndex]
                  : TEXT.stepEtc[nationIndex]}
              </StepItem>
            </StepList>
          )}
        </Header>
        <Body>
          <BodyText>
            {isCruise
              ? TEXT.bodyTextCruise[nationIndex]
              : TEXT.bodyText[nationIndex]}
          </BodyText>
          <ReceiptList>
            {receiptData?.map((receipt, index) => {
              const dateOfPurchase = receipt?.saleDate.split(' ')[0] || '-';
              const payment =
                Number(receipt?.totalAmount).toLocaleString() || '0';
              const refund =
                Number(receipt?.totalRefund).toLocaleString() || '0';
              // const receiptData = reformReceiptData(receipt, passportNumber!);
              const printForm = formatPrintReceiptForm(
                receiptData!,
                passportNumber as string,
              );
              const selectedReceiptInfo = printForm?.find(
                (d) => d.refundIndex === receipt.refundIndex,
              );

              return (
                <ReceiptSummary
                  key={index}
                  nationality={nationality}
                  refundIndex={receipt.refundIndex}
                  dateOfPurchase={dateOfPurchase}
                  name={receipt.franchiseeName || '-'}
                  payment={payment}
                  refund={refund}
                  imgSrc={IMG_RECEIPT_PREVIEW2}
                  btnTitle={
                    nationality === 'KOR' ? '영수증 보기' : 'View Receipt'
                  }
                  onClick={() => setReceiptInfo(selectedReceiptInfo)}
                />
              );
            })}
          </ReceiptList>
        </Body>
        <Footer>
          <Button
            size='lg'
            variant='line'
            style={{
              width: '90px',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.28px',
              lineHeight: '150%',
            }}
            onClick={() =>
              history.push(
                isCruise ? routes.locationPrintCruise : routes.locationPrint,
              )
            }
          >
            {TEXT.printButton[nationIndex]}
          </Button>
          <Button onClick={() => setIsOpenMailModal(true)} size='lg'>
            {TEXT.sendButton[nationIndex]}
          </Button>
        </Footer>
      </ReceiptLayout>
      {isOpenMailModal && (
        <ReceiptMailModal
          isOpen={isOpenMailModal}
          onClose={() => setIsOpenMailModal(false)}
          receiptInfo={formatPrintReceiptForm(
            receiptData!,
            passportNumber as string,
          )}
        />
      )}
      {receiptInfo && (
        <Popover
          isOpen={!!receiptInfo}
          hasBackdrop
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100vw',
            backgroundColor: 'transparent',
          }}
        >
          <ReceiptForPrint
            info={receiptInfo}
            order={1}
            lastIndex={0}
            isPreview
          />
          <Button
            enabled
            onClick={() => setReceiptInfo(null)}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '335px',
              maxWidth: '80vw',
              marginTop: '200px',
            }}
          >
            {nationality === 'KOR' ? '닫기' : 'Close'}
          </Button>
        </Popover>
      )}
    </>
  );
}

const Header = styled.header`
  position: fixed;
  top: 83px;
  left: 0;
  width: 100%;
  padding: 20px;
  background-color: #fff;
  z-index: 1;
  &::after {
    position: absolute;
    bottom: 0;
    left: -20px;
    content: '';
    width: calc(100% + 40px);
    height: 10px;
    box-shadow: 0px 4px 4px 0px rgba(216, 216, 216, 0.25);
  }
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--KTP-Grayscale-Gray_02, #111);
  font-size: 20px;
  font-weight: 700;
  line-height: 140%; /* 28px */
  letter-spacing: -0.4px;
`;

const ToggleButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 0;
`;

const StepList = styled.ul`
  position: absolute;
  right: 10px;
  display: flex;
  flex-direction: column;
  width: 250px;
  gap: 8px;
  padding: 16px;
  margin-top: 4px;
  background-color: #fff;
  border: 1.5px solid #246cf6;
  border-radius: 12px;
  z-index: 1;
`;

const StepTitle = styled.div`
  color: var(--KTP-Grayscale-Gray_03, #333);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #3a3b3e;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
`;

const Count = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #5f6165;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
`;

const Body = styled.main`
  width: 100%;
  height: calc(100% - 124px - 16px); // footer, gap, title height
  margin: 70px 0 84px; // title height
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BodyText = styled.p`
  color: var(--KTP-Grayscale-Gray_03, #333);
  font-size: 18px;
  font-weight: 600;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;
  margin-top: 13px;
  white-space: pre-line;
`;

const ReceiptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 30px 0 40px;
`;

const Footer = styled.footer`
  display: flex;
  gap: 12px;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 8px 20px 36px 20px;
  box-shadow: 0px -4px 8px 0px rgba(106, 106, 106, 0.25);
  background-color: #fff;
`;

export default AirportPrintReceipt;
